import {
  loadWalmartStores,
  loadHomeDepotStores,
  loadDollarGeneralStores,
  loadTargetStores,
  loadBestBuyStores,
  loadCostcoStores,
  loadLowesStores,
  loadTractorSupplyStores,
  loadNorthernToolStores,
  loadSamsClubStores,
  storesLoader,
} from "./storesLoader";

// Store interfaces
export interface WalmartStore {
  store_id: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
  geo?: { lat: number; long: number };
}

export interface DollarGeneralStore {
  storeNumber: string;
  address: string;
  city: string;
  state: string;
  latitude: string;
  longitude: string;
  postal_code: string;
  geo?: { lat: number; long: number };
}

export interface HomeDepotStore {
  Id: string;
  store_number: string;
  store_title: string;
  address_1: string;
  address_2: string | null;
  address_3: string | null;
  city: string;
  state: string;
  zip: string;
  postal_code?: string;
  phone: string;
  latitude: string;
  longitude: string;
}

export interface TargetStore {
  "Store No.": number;
  Name: string;
  Latitude: number;
  Longitude: number;
  Street: string;
  City: string;
  State: string;
  Zip_Code: number;
  label?: string;
}

export type LowesStore = TargetStore;

export interface TractorSupplyStore {
  store_number: string;
  name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone?: string;
  url?: string;
  label: string;
  latitude: number;
  longitude: number;
  geocode_source?: "census_address" | "zcta_centroid";
}

export type LocalClearanceStore = TractorSupplyStore;

export interface BestBuyStore {
  store_number: string;
  name: string;
  latitude: number;
  longitude: number;
  street: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  url: string;
}

export interface CostcoStore {
  warehouse_number: number;
  warehouse_name: string;
  region: string;
  address: string;
  location: string;
  latitude: number | null;
  longitude: number | null;
  geocodedAddress: string | null;
  timezone: string | null;
}

// Unified store interface for consistent handling
export interface UnifiedStore {
  id: string;
  displayId: string;
  displayName: string;
  address: string;
  city: string;
  state: string;
  postalCode: string;
  retailer: "walmart" | "home_depot" | "dollar_general" | "target" | "bestbuy" | "costco" | "lowes" | "tractor_supply" | "northern_tool" | "samsclub";
  geo?: { lat: number; long: number };
  originalData: WalmartStore | HomeDepotStore | DollarGeneralStore | TargetStore | BestBuyStore | CostcoStore | LowesStore | TractorSupplyStore | LocalClearanceStore;
}

/**
 * Formats Home Depot store number by removing leading zeros
 * @param storeNumber - The store number to format
 * @returns Formatted store number without leading zeros
 */
export function formatHomeDepotStoreId(
  storeNumber: string | null | undefined
): string {
  if (!storeNumber) return "";
  return storeNumber.replace(/^0+/, "") || storeNumber;
}

/**
 * Gets a Walmart store by its store_id (async version from S3)
 * @param storeId - The Walmart store_id
 * @returns Promise resolving to the Walmart store or null if not found
 */
export async function getWalmartStoreByIdAsync(
  storeId: string
): Promise<WalmartStore | null> {
  const stores = await loadWalmartStores<WalmartStore[]>();
  return stores.find((store) => store.store_id === storeId) || null;
}

/**
 * Gets a Walmart store by its store_id (sync version, uses cached data only)
 * @param storeId - The Walmart store_id
 * @returns The Walmart store or null if not found
 */
export function getWalmartStoreById(storeId: string): WalmartStore | null {
  // Try cached data only (stores must be loaded from S3 first)
  const cached = storesLoader.getCachedStores<WalmartStore[]>("walmart");
  if (cached) {
    return cached.find((store) => store.store_id === storeId) || null;
  }
  // No cached data available - return null
  return null;
}

/**
 * Gets a Home Depot store by its store_number (async version from S3)
 * @param storeNumber - The Home Depot store_number (can be with or without leading zeros)
 * @returns Promise resolving to the Home Depot store or null if not found
 */
export async function getHomeDepotStoreByNumberAsync(
  storeNumber: string
): Promise<HomeDepotStore | null> {
  const stores = await loadHomeDepotStores<HomeDepotStore[]>();
  return findHomeDepotStore(stores, storeNumber);
}

/**
 * Gets a Home Depot store by its store_number (sync version, uses cached data only)
 * @param storeNumber - The Home Depot store_number (can be with or without leading zeros)
 * @returns The Home Depot store or null if not found
 */
export function getHomeDepotStoreByNumber(
  storeNumber: string
): HomeDepotStore | null {
  // Try cached data only (stores must be loaded from S3 first)
  const cached = storesLoader.getCachedStores<HomeDepotStore[]>("home_depot");
  if (cached) {
    return findHomeDepotStore(cached, storeNumber);
  }
  // No cached data available - return null
  return null;
}

function findHomeDepotStore(
  stores: HomeDepotStore[],
  storeNumber: string
): HomeDepotStore | null {
  // Try exact match first
  let store = stores.find((s) => s.store_number === storeNumber);

  // If not found, try with padded version (add leading zero if 3 digits)
  if (!store && /^\d{3}$/.test(storeNumber)) {
    const paddedNumber = storeNumber.padStart(4, "0");
    store = stores.find((s) => s.store_number === paddedNumber);
  }

  // If still not found, try with formatted version (remove leading zeros)
  if (!store) {
    const formattedNumber = formatHomeDepotStoreId(storeNumber);
    store = stores.find(
      (s) => formatHomeDepotStoreId(s.store_number) === formattedNumber
    );
  }

  return store || null;
}

export function getDollarGeneralGeo(
  store: DollarGeneralStore
): { lat: number; long: number } | undefined {
  const x = parseFloat(store.latitude);
  const y = parseFloat(store.longitude);
  if (Number.isNaN(x) || Number.isNaN(y)) return store.geo;
  const { lat, lon } = toLatLon(x, y);
  return { lat, long: lon };
}

function distanceMiles(
  a: { lat: number; long: number },
  b: { lat: number; long: number }
): number {
  const R = 3959;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.long - a.long) * Math.PI) / 180;
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h =
    sinDLat * sinDLat +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      sinDLon *
      sinDLon;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Resolves duplicate store numbers by proximity when multiple catalog entries share an ID. */
export function findDollarGeneralStoreInList(
  stores: DollarGeneralStore[],
  storeNumber: string,
  preferNear?: { lat: number; long: number }
): DollarGeneralStore | null {
  const matches = stores.filter((store) => store.storeNumber === storeNumber);
  if (!matches.length) return null;
  if (matches.length === 1 || !preferNear) return matches[0];

  let best = matches[0];
  let bestDistance = Infinity;
  for (const store of matches) {
    const geo = getDollarGeneralGeo(store);
    if (!geo) continue;
    const miles = distanceMiles(preferNear, geo);
    if (miles < bestDistance) {
      bestDistance = miles;
      best = store;
    }
  }
  return best;
}

/**
 * Gets a Dollar General store by its store_number (async version from S3)
 */
export async function getDollarGeneralStoreByNumberAsync(
  storeNumber: string,
  preferNear?: { lat: number; long: number }
): Promise<DollarGeneralStore | null> {
  const stores = await loadDollarGeneralStores<DollarGeneralStore[]>();
  return findDollarGeneralStoreInList(stores, storeNumber, preferNear);
}

/**
 * Gets a Dollar General store by its store_number (sync version, uses cached data only)
 */
export function getDollarGeneralStoreByNumber(
  storeNumber: string,
  preferNear?: { lat: number; long: number }
): DollarGeneralStore | null {
  const cached = storesLoader.getCachedStores<DollarGeneralStore[]>("dollar_general");
  if (!cached) return null;
  return findDollarGeneralStoreInList(cached, storeNumber, preferNear);
}

/**
 * Gets a Target store by its store number (async version from S3)
 */
export async function getTargetStoreByIdAsync(storeId: string): Promise<TargetStore | null> {
  const stores = await loadTargetStores<TargetStore[]>();
  return stores.find((s: any) => String(s["Store No."] || s.store_id) === storeId) || null;
}

/**
 * Gets a Target store by its store number (sync version, uses cached data only)
 */
export function getTargetStoreById(storeId: string): TargetStore | null {
  const cached = storesLoader.getCachedStores<TargetStore[]>("target");
  return cached?.find((s: any) => String(s["Store No."] || s.store_id) === storeId) || null;
}

export async function getLowesStoreByIdAsync(storeId: string): Promise<LowesStore | null> {
  const stores = await loadLowesStores<LowesStore[]>();
  return stores.find((s) => String(s["Store No."]) === storeId) || null;
}

export function getLowesStoreById(storeId: string): LowesStore | null {
  const cached = storesLoader.getCachedStores<LowesStore[]>("lowes");
  return cached?.find((s) => String(s["Store No."]) === storeId) || null;
}

export async function getTractorSupplyStoreByIdAsync(storeId: string): Promise<TractorSupplyStore | null> {
  const stores = await loadTractorSupplyStores<TractorSupplyStore[]>();
  return stores.find((s) => String(s.store_number).replace(/^0+/, "") === storeId.replace(/^0+/, "")) || null;
}

export function getTractorSupplyStoreById(storeId: string): TractorSupplyStore | null {
  const cached = storesLoader.getCachedStores<TractorSupplyStore[]>("tractor_supply");
  return cached?.find((s) => String(s.store_number).replace(/^0+/, "") === storeId.replace(/^0+/, "")) || null;
}

export async function getLocalClearanceStoreByIdAsync(
  storeId: string,
  retailer: "northern_tool" | "samsclub" = "northern_tool"
): Promise<LocalClearanceStore | null> {
  const stores = retailer === "samsclub"
    ? await loadSamsClubStores<LocalClearanceStore[]>()
    : await loadNorthernToolStores<LocalClearanceStore[]>();
  const normalize = (value: string) => value.replace(/^0+/, "") || value;
  return stores.find((store) => normalize(String(store.store_number)) === normalize(storeId)) || null;
}

export function getLocalClearanceStoreById(
  storeId: string,
  retailer: "northern_tool" | "samsclub" = "northern_tool"
): LocalClearanceStore | null {
  const cached = storesLoader.getCachedStores<LocalClearanceStore[]>(retailer);
  const normalize = (value: string) => value.replace(/^0+/, "") || value;
  return cached?.find((store) => normalize(String(store.store_number)) === normalize(storeId)) || null;
}

/**
 * Gets a Best Buy store by its store_number (async version from S3)
 */
export async function getBestBuyStoreByNumberAsync(storeNumber: string): Promise<BestBuyStore | null> {
  const stores = await loadBestBuyStores<BestBuyStore[]>();
  return stores.find((store) => store.store_number === storeNumber) || null;
}

/**
 * Gets a Best Buy store by its store_number (sync version, uses cached data only)
 */
export function getBestBuyStoreByNumber(storeNumber: string): BestBuyStore | null {
  const cached = storesLoader.getCachedStores<BestBuyStore[]>("bestbuy");
  return cached?.find((store) => store.store_number === storeNumber) || null;
}

/**
 * Gets a Costco store by its warehouse_number (async version from S3)
 * @param storeId - Either "88-wh" format or plain number "88"
 */
export async function getCostcoStoreByIdAsync(storeId: string): Promise<CostcoStore | null> {
  const stores = await loadCostcoStores<CostcoStore[]>();
  const num = parseInt(storeId.replace(/-wh$/, ""), 10);
  return stores.find((store) => store.warehouse_number === num) || null;
}

/**
 * Gets a Costco store by its warehouse_number (sync version, uses cached data only)
 * @param storeId - Either "88-wh" format or plain number "88"
 */
export function getCostcoStoreById(storeId: string): CostcoStore | null {
  const cached = storesLoader.getCachedStores<CostcoStore[]>("costco");
  const num = parseInt(storeId.replace(/-wh$/, ""), 10);
  return cached?.find((store) => store.warehouse_number === num) || null;
}

/**
 * Gets a store by ID for any retailer
 */
export function getStoreById(
  storeId: string,
  retailer: "walmart" | "home_depot" | "dollar_general" | "target" | "bestbuy" | "costco" | "lowes" | "tractor_supply" | "northern_tool" | "samsclub"
): UnifiedStore | null {
  if (retailer === "walmart") {
    const store = getWalmartStoreById(storeId);
    if (!store) return null;
    return {
      id: store.store_id, displayId: store.store_id, displayName: store.address,
      address: store.address, city: store.city, state: store.state,
      postalCode: store.postal_code, retailer: "walmart", geo: store.geo, originalData: store,
    };
  } else if (retailer === "home_depot") {
    const store = getHomeDepotStoreByNumber(storeId);
    if (!store) return null;
    const formattedId = formatHomeDepotStoreId(store.store_number);
    return {
      id: formattedId, displayId: formattedId, displayName: store.store_title,
      address: `${store.address_1}, ${store.city}, ${store.state} ${store.zip}`,
      city: store.city, state: store.state, postalCode: store.zip, retailer: "home_depot",
      geo: { lat: parseFloat(store.latitude), long: parseFloat(store.longitude) },
      originalData: store,
    };
  } else if (retailer === "dollar_general") {
    const store = getDollarGeneralStoreByNumber(storeId);
    if (!store) return null;
    return {
      id: store.storeNumber, displayId: store.storeNumber, displayName: store.address,
      address: store.address, city: store.city, state: store.state,
      postalCode: store.postal_code, retailer: "dollar_general",
      geo: getDollarGeneralGeo(store),
      originalData: store,
    };
  } else if (retailer === "bestbuy") {
    const store = getBestBuyStoreByNumber(storeId);
    if (!store) return null;
    return {
      id: store.store_number, displayId: store.store_number, displayName: store.name,
      address: `${store.street}, ${store.city}, ${store.state} ${store.zip}`,
      city: store.city, state: store.state, postalCode: store.zip, retailer: "bestbuy",
      geo: { lat: store.latitude, long: store.longitude }, originalData: store,
    };
  } else if (retailer === "costco") {
    const store = getCostcoStoreById(storeId);
    if (!store) return null;
    const whId = `${store.warehouse_number}-wh`;
    return {
      id: whId, displayId: whId, displayName: store.warehouse_name,
      address: `${store.address}, ${store.location}`,
      city: store.location.split(",")[0]?.trim() || "", state: store.location.split(",")[1]?.trim() || "",
      postalCode: "", retailer: "costco",
      geo: store.latitude && store.longitude ? { lat: store.latitude, long: store.longitude } : undefined,
      originalData: store,
    };
  } else if (retailer === "lowes") {
    const store = getLowesStoreById(storeId);
    if (!store) return null;
    const id = String(store["Store No."]);
    return {
      id, displayId: id, displayName: store.Name || "",
      address: `${store.Street}, ${store.City}, ${store.State}`,
      city: store.City, state: store.State,
      postalCode: String(store.Zip_Code || ""),
      retailer: "lowes",
      geo: store.Latitude ? { lat: store.Latitude, long: store.Longitude } : undefined,
      originalData: store,
    };
  } else if (retailer === "tractor_supply") {
    const store = getTractorSupplyStoreById(storeId);
    if (!store) return null;
    const id = String(store.store_number).replace(/^0+/, "") || String(store.store_number);
    return {
      id, displayId: id, displayName: store.label || store.name,
      address: `${store.street}, ${store.city}, ${store.state} ${store.zip}`,
      city: store.city, state: store.state, postalCode: store.zip,
      retailer: "tractor_supply", geo: { lat: store.latitude, long: store.longitude }, originalData: store,
    };
  } else if (retailer === "northern_tool" || retailer === "samsclub") {
    const store = getLocalClearanceStoreById(storeId, retailer);
    if (!store) return null;
    const id = String(store.store_number).replace(/^0+/, "") || String(store.store_number);
    return {
      id, displayId: id, displayName: store.label || store.name,
      address: `${store.street}, ${store.city}, ${store.state} ${store.zip}`,
      city: store.city, state: store.state, postalCode: store.zip,
      retailer, geo: { lat: store.latitude, long: store.longitude }, originalData: store,
    };
  } else {
    const store = getTargetStoreById(storeId) as any;
    if (!store) return null;
    const id = String(store["Store No."] || store.store_id || "");
    return {
      id, displayId: id, displayName: store.Name || store.location_name || "",
      address: `${store.Street || store.mailing_address?.address_line1 || ""}, ${store.City || store.mailing_address?.city || ""}, ${store.State || store.mailing_address?.state || ""}`,
      city: store.City || store.mailing_address?.city || "",
      state: store.State || store.mailing_address?.state || "",
      postalCode: String(store.Zip_Code || store.mailing_address?.postal_code?.split("-")[0] || ""),
      retailer: "target",
      geo: store.Latitude ? { lat: Number(store.Latitude), long: Number(store.Longitude) } : store.geo,
      originalData: store,
    };
  }
}

/**
 * Gets multiple stores by their IDs for any retailer
 */
export function getStoresByIds(
  storeIds: string[],
  retailer: "walmart" | "home_depot" | "dollar_general" | "target" | "bestbuy" | "costco" | "lowes" | "tractor_supply" | "northern_tool" | "samsclub"
): UnifiedStore[] {
  return storeIds.map((id) => getStoreById(id, retailer)).filter((s): s is UnifiedStore => s !== null);
}

export function toLatLon(x: number, y: number) {
  const R = 6378137.0; // Earth's radius in meters (WGS84)
  const lon = (x / R) * (180 / Math.PI);
  const lat = (Math.atan(Math.exp(y / R)) - Math.PI / 4) * 2 * (180 / Math.PI);
  return { lon, lat };
}
/**
 * Gets all available stores for a retailer (async version from S3)
 */
export async function getAllStoresForRetailerAsync(
  retailer: "walmart" | "home_depot" | "dollar_general" | "target" | "bestbuy" | "costco" | "lowes" | "tractor_supply" | "northern_tool" | "samsclub"
): Promise<UnifiedStore[]> {
  if (retailer === "walmart") {
    const stores = await loadWalmartStores<WalmartStore[]>();
    return stores.map((s) => ({
      id: s.store_id, displayId: s.store_id, displayName: s.address, address: s.address,
      city: s.city, state: s.state, postalCode: s.postal_code, retailer: "walmart",
      geo: s.geo, originalData: s,
    }));
  } else if (retailer === "dollar_general") {
    const stores = await loadDollarGeneralStores<DollarGeneralStore[]>();
    return stores.map((s) => ({
      id: s.storeNumber, displayId: s.storeNumber, displayName: s.address, address: s.address,
      city: s.city, state: s.state, postalCode: s.postal_code, retailer: "dollar_general",
      geo: getDollarGeneralGeo(s),
      originalData: s,
    }));
  } else if (retailer === "target") {
    const stores = await loadTargetStores<TargetStore[]>();
    return stores.map((s: any) => {
      const id = String(s["Store No."] || s.store_id || "");
      return {
        id, displayId: id, displayName: s.Name || s.location_name || "",
        address: `${s.Street || s.mailing_address?.address_line1 || ""}, ${s.City || s.mailing_address?.city || ""}, ${s.State || s.mailing_address?.state || ""}`,
        city: s.City || s.mailing_address?.city || "",
        state: s.State || s.mailing_address?.state || "",
        postalCode: String(s.Zip_Code || s.mailing_address?.postal_code?.split("-")[0] || ""),
        retailer: "target",
        geo: s.Latitude ? { lat: Number(s.Latitude), long: Number(s.Longitude) } : s.geo,
        originalData: s,
      };
    });
  } else if (retailer === "bestbuy") {
    const stores = await loadBestBuyStores<BestBuyStore[]>();
    return stores.map((s) => ({
      id: s.store_number, displayId: s.store_number, displayName: s.name,
      address: `${s.street}, ${s.city}, ${s.state} ${s.zip}`,
      city: s.city, state: s.state, postalCode: s.zip, retailer: "bestbuy",
      geo: { lat: s.latitude, long: s.longitude }, originalData: s,
    }));
  } else if (retailer === "costco") {
    const stores = await loadCostcoStores<CostcoStore[]>();
    return stores.map((s) => {
      const whId = `${s.warehouse_number}-wh`;
      return {
        id: whId, displayId: whId, displayName: s.warehouse_name,
        address: `${s.address}, ${s.location}`,
        city: s.location.split(",")[0]?.trim() || "", state: s.location.split(",")[1]?.trim() || "",
        postalCode: "", retailer: "costco" as const,
        geo: s.latitude && s.longitude ? { lat: s.latitude, long: s.longitude } : undefined,
        originalData: s,
      };
    });
  } else if (retailer === "lowes") {
    const stores = await loadLowesStores<LowesStore[]>();
    return stores.map((s) => {
      const id = String(s["Store No."]);
      return {
        id, displayId: id, displayName: s.Name || "",
        address: `${s.Street}, ${s.City}, ${s.State}`,
        city: s.City, state: s.State, postalCode: String(s.Zip_Code || ""),
        retailer: "lowes" as const,
        geo: s.Latitude ? { lat: s.Latitude, long: s.Longitude } : undefined,
        originalData: s,
      };
    });
  } else if (retailer === "tractor_supply") {
    const stores = await loadTractorSupplyStores<TractorSupplyStore[]>();
    return stores.map((s) => {
      const id = String(s.store_number).replace(/^0+/, "") || String(s.store_number);
      return {
        id, displayId: id, displayName: s.label || s.name,
        address: `${s.street}, ${s.city}, ${s.state} ${s.zip}`,
        city: s.city, state: s.state, postalCode: s.zip,
        retailer: "tractor_supply" as const,
        geo: { lat: s.latitude, long: s.longitude }, originalData: s,
      };
    });
  } else if (retailer === "northern_tool" || retailer === "samsclub") {
    const stores = retailer === "samsclub"
      ? await loadSamsClubStores<LocalClearanceStore[]>()
      : await loadNorthernToolStores<LocalClearanceStore[]>();
    return stores.map((store) => {
      const id = String(store.store_number).replace(/^0+/, "") || String(store.store_number);
      return {
        id, displayId: id, displayName: store.label || store.name,
        address: `${store.street}, ${store.city}, ${store.state} ${store.zip}`,
        city: store.city, state: store.state, postalCode: store.zip,
        retailer, geo: { lat: store.latitude, long: store.longitude }, originalData: store,
      };
    });
  } else {
    const stores = await loadHomeDepotStores<HomeDepotStore[]>();
    return stores.map((s) => {
      const id = formatHomeDepotStoreId(s.store_number);
      return {
        id, displayId: id, displayName: s.store_title,
        address: `${s.address_1}, ${s.city}, ${s.state} ${s.zip}`,
        city: s.city, state: s.state, postalCode: s.zip, retailer: "home_depot",
        geo: { lat: parseFloat(s.latitude), long: parseFloat(s.longitude) }, originalData: s,
      };
    });
  }
}

/**
 * Gets all available stores for a retailer (sync version, uses cached data only)
 */
export function getAllStoresForRetailer(
  retailer: "walmart" | "home_depot" | "dollar_general" | "target" | "bestbuy" | "costco"
): UnifiedStore[] {
  if (retailer === "walmart") {
    const cached = storesLoader.getCachedStores<WalmartStore[]>("walmart");
    if (!cached) return [];
    return cached.map((s) => ({
      id: s.store_id, displayId: s.store_id, displayName: s.address, address: s.address,
      city: s.city, state: s.state, postalCode: s.postal_code, retailer: "walmart",
      geo: s.geo, originalData: s,
    }));
  } else if (retailer === "dollar_general") {
    const cached = storesLoader.getCachedStores<DollarGeneralStore[]>("dollar_general");
    if (!cached) return [];
    return cached.map((s) => ({
      id: s.storeNumber, displayId: s.storeNumber, displayName: s.address, address: s.address,
      city: s.city, state: s.state, postalCode: s.postal_code, retailer: "dollar_general",
      geo: getDollarGeneralGeo(s),
      originalData: s,
    }));
  } else if (retailer === "target") {
    const cached = storesLoader.getCachedStores<TargetStore[]>("target");
    if (!cached) return [];
    return cached.map((s: any) => {
      const id = String(s["Store No."] || s.store_id || "");
      return {
        id, displayId: id, displayName: s.Name || s.location_name || "",
        address: `${s.Street || s.mailing_address?.address_line1 || ""}, ${s.City || s.mailing_address?.city || ""}, ${s.State || s.mailing_address?.state || ""}`,
        city: s.City || s.mailing_address?.city || "",
        state: s.State || s.mailing_address?.state || "",
        postalCode: String(s.Zip_Code || s.mailing_address?.postal_code?.split("-")[0] || ""),
        retailer: "target",
        geo: s.Latitude ? { lat: Number(s.Latitude), long: Number(s.Longitude) } : s.geo,
        originalData: s,
      };
    });
  } else if (retailer === "bestbuy") {
    const cached = storesLoader.getCachedStores<BestBuyStore[]>("bestbuy");
    if (!cached) return [];
    return cached.map((s) => ({
      id: s.store_number, displayId: s.store_number, displayName: s.name,
      address: `${s.street}, ${s.city}, ${s.state} ${s.zip}`,
      city: s.city, state: s.state, postalCode: s.zip, retailer: "bestbuy",
      geo: { lat: s.latitude, long: s.longitude }, originalData: s,
    }));
  } else if (retailer === "costco") {
    const cached = storesLoader.getCachedStores<CostcoStore[]>("costco");
    if (!cached) return [];
    return cached.map((s) => {
      const whId = `${s.warehouse_number}-wh`;
      return {
        id: whId, displayId: whId, displayName: s.warehouse_name,
        address: `${s.address}, ${s.location}`,
        city: s.location.split(",")[0]?.trim() || "", state: s.location.split(",")[1]?.trim() || "",
        postalCode: "", retailer: "costco" as const,
        geo: s.latitude && s.longitude ? { lat: s.latitude, long: s.longitude } : undefined,
        originalData: s,
      };
    });
  } else {
    const cached = storesLoader.getCachedStores<HomeDepotStore[]>("home_depot");
    if (!cached) return [];
    return cached.map((s) => {
      const id = formatHomeDepotStoreId(s.store_number);
      return {
        id, displayId: id, displayName: s.store_title,
        address: `${s.address_1}, ${s.city}, ${s.state} ${s.zip}`,
        city: s.city, state: s.state, postalCode: s.zip, retailer: "home_depot",
        geo: { lat: parseFloat(s.latitude), long: parseFloat(s.longitude) }, originalData: s,
      };
    });
  }
}

/**
 * Formats a store for display purposes
 */
export function formatStoreDisplay(store: UnifiedStore): string {
  if (store.retailer === "walmart" || store.retailer === "dollar_general") {
    return `${store.address} - ${store.postalCode}`;
  }
  return `${store.displayName} - ${store.city}, ${store.state} ${store.postalCode}`;
}

/**
 * Gets the correct store ID property name for a retailer
 */
export function getStoreIdProperty(
  retailer: "walmart" | "home_depot" | "dollar_general" | "target" | "bestbuy" | "costco"
): string {
  const props: Record<string, string> = {
    walmart: "store_id", home_depot: "store_number",
    dollar_general: "storeNumber", target: "store_id", bestbuy: "store_number",
    costco: "warehouse_number",
  };
  return props[retailer] || "";
}
